import { Typography } from '@mui/material'
import { EditDialog, EditInDialogButton } from '@react-admin/ra-form-layout'
import { useTranslate } from 'ra-core'
import React, { cloneElement, useState } from 'react'
import {
  Edit,
  EditButton,
  ResourceContextProvider,
  UPDATE,
  useCreatePath,
  useNotify,
  useRecordContext,
  useResourceContext,
} from 'react-admin'

import { getResourceSuccessMessage } from '.'
import { useHandleRedirection } from './hooks'
import useInvalidateGroupMembershipRecord from './useInvalidateGroupMembershipRecord'
import LockedForUpdateIcon from '../../components/icons/LockedForUpdateIcon'
import { SYSTEM_PERMISSION_UPDATE } from '../../config/permissions'
import { useIsFieldPermissionDenied, useResourcePermissions } from '../../domain/permissions'
import { useSmallScreen } from '../../utils/theme'

export default ({ formLayoutComponent, transformValues }) => {
  const handleRedirection = useHandleRedirection()
  const invalidateMemberships = useInvalidateGroupMembershipRecord()
  const notify = useNotify()
  const resource = useResourceContext()

  const onSuccess = async (record) => {
    await invalidateMemberships(record)
    await handleRedirection(record)
    await notify(getResourceSuccessMessage(resource, 'forms', 'update'))
  }
  const onError = (error) => notify(error.message, { type: 'error' })

  return (
    <Edit mutationMode="pessimistic" mutationOptions={{ onSuccess, onError }} transform={transformValues}>
      {formLayoutComponent}
    </Edit>
  )
}

export const AdvancedEditInDialogButton = ({ children: formLayout, condition, title, ...props }) => {
  const record = useRecordContext()
  const resource = useResourceContext()
  const translate = useTranslate()
  const [hasEdit] = useResourcePermissions(resource, SYSTEM_PERMISSION_UPDATE)
  const canNotEditLockedResource = useIsFieldPermissionDenied({
    action: SYSTEM_PERMISSION_UPDATE,
    source: 'locked_for_update',
  })
  title = title || `resources.${resource}.forms.update.title`

  const notify = useNotify()
  const invalidateMemberships = useInvalidateGroupMembershipRecord()
  const onSuccess = async (record) => {
    await invalidateMemberships(record)
    notify(getResourceSuccessMessage(resource, 'forms', 'update'))
  }
  const onError = (error) => notify(error.message, { type: 'error' })

  if (!record || (typeof condition === 'function' && !condition(record)) || !hasEdit) {
    return null
  }

  return canNotEditLockedResource && record.locked_for_update === true ? (
    <LockedForUpdateIcon />
  ) : (
    <EditInDialogButton
      mutationOptions={{ onSuccess, onError }}
      mutationMode="pessimistic"
      onClick={(e) => e.stopPropagation()}
      sx={{ '& .MuiDialogTitle-root': { borderBottom: '1px solid', borderColor: 'divider' } }}
      title={<Typography variant="h6">{translate(title)}</Typography>}
      {...props}
    >
      {cloneElement(formLayout, { type: UPDATE })}
    </EditInDialogButton>
  )
}

export const AdvancedEditButton = ({ children: formLayout, id, title, to, ...props }) => {
  const createPath = useCreatePath()
  const resource = useResourceContext(props)
  const [hasEdit] = useResourcePermissions(resource, SYSTEM_PERMISSION_UPDATE)
  const isSmallScreen = useSmallScreen()

  if (!hasEdit) {
    return null
  }

  return isSmallScreen ? (
    <EditButton to={to ?? { pathname: createPath({ resource, id, type: 'edit' }) }} />
  ) : (
    <AdvancedEditInDialogButton title={title} {...props}>
      {formLayout}
    </AdvancedEditInDialogButton>
  )
}

export const useEditResource = (resource, formLayout, transformValues) => {
  const [editPopupState, setEditPopupState] = useState({ isOpen: false, values: {} })
  const handleEditPopupOpen = (record) => setEditPopupState({ isOpen: true, values: record })
  const handleEditPopupClose = () => setEditPopupState({ isOpen: false, values: {} })

  const notify = useNotify()
  const invalidateMemberships = useInvalidateGroupMembershipRecord(resource)
  const onSuccess = async (record) => {
    await invalidateMemberships(record)
    notify(getResourceSuccessMessage(resource, 'forms', 'update'))
  }
  const onError = (error) => notify(error.message, { type: 'error' })

  const dialog = (
    <ResourceContextProvider value={resource}>
      <EditDialog
        close={handleEditPopupClose}
        id={editPopupState.values.id}
        isOpen={editPopupState.isOpen}
        mutationMode="pessimistic"
        mutationOptions={{ onSuccess, onError }}
        open={handleEditPopupOpen}
        sx={{ '& .MuiDialogTitle-root': { borderBottom: '1px solid', borderColor: 'divider' } }}
        title={`resources.${resource}.forms.update.title`}
        transform={transformValues}
      >
        {cloneElement(formLayout, { record: editPopupState.values, type: UPDATE })}
      </EditDialog>
    </ResourceContextProvider>
  )

  return [handleEditPopupOpen, dialog]
}
